import * as React from 'react';
import {
  ImageSourcePropType,
  ImageStyle,
  ImageURISource,
  StyleSheet,
  View,
  ViewStyle,
} from 'react-native';

import EditableLaterMessage from '../../../shared/EditableLaterMessage';
import PrimaryButton from '../../../shared/buttons/PrimaryButton';
import DefaultOutlineButton from '../../../shared/buttons/DefaultOutlineButton';
import LabelWithOption from '../../../shared/forms/LabelWithOption';
import CoverPicker from '../../../shared/image_picker/CoverPicker';
import DimensionContext from '../../../shared/dimension/DimensionContext';
import AlertModal from '../../../shared/modals/AlertModal';
import TooltipModal from '../../../shared/modals/TooltipModal';
import MaterialRightCheck from '../../../shared/MaterialRightCheck';

import CoverIllustratorFormField from '../../image/partials/CoverIllustratorFormField';

import convertImageSource from '../../../../helpers/convertImageSource';

import File from '../../../../../domain/entities/File';
import StoryForm from '../../../../../domain/forms/StoryForm';

import storyCoverImageUri from '../../../../../assets/images/tips/story_cover_image.png';
import storyCoverIllustratorUri from '../../../../../assets/images/tips/story_cover_illustrator.png';

type TooltipModalType = 'cover_image' | 'cover_illustrator';

const TypeToTooltipInfo = {
  cover_image: {
    title: '表紙',
    description:
      '様々なページで表示される表紙画像となります。\n読者が読みたくなるような表紙を設定しましょう。',
    source: convertImageSource(storyCoverImageUri),
    imageStyle: {width: 279, height: 240},
  },
  cover_illustrator: {
    title: 'イラストレーター名',
    description: '表紙に、担当したイラストレーター名を記載することができます。',
    source: convertImageSource(storyCoverIllustratorUri),
    imageStyle: {width: 279, height: 180},
  },
} as {
  [key in TooltipModalType]: {
    title: string;
    description: string;
    source: ImageSourcePropType;
    imageStyle?: ImageStyle;
  };
};

interface Props {
  storyForm: StoryForm;
  buttonName?: string;
  onChangeImage: (file: File) => void;
  onChangeCoverIllustrator?: (coverIllustrator: string) => void;
  onSubmit: () => void;
  onRequestReset: () => void;
  onForwardToImageSelection: () => void;
  onForwardToCharacterImageSelection: () => void;
  onForwardToTextInput: () => void;
}

const Form: React.FunctionComponent<Props> = props => {
  const {
    storyForm,
    buttonName,
    onChangeImage,
    onChangeCoverIllustrator,
    onSubmit,
    onRequestReset,
    onForwardToImageSelection,
    onForwardToCharacterImageSelection,
    onForwardToTextInput,
  } = props;
  let coverSource: ImageURISource | ImageURISource[] | null = null;
  let disabled = true;
  const [confirmedMaterialRight, setConfirmedMaterialRight] =
    React.useState(false);
  if (storyForm.coverImage) {
    coverSource = {uri: storyForm.coverImage.uri};
    disabled = false;
  } else if (storyForm.hasCoverImage && storyForm.originalCoverImageUrl) {
    coverSource = {uri: storyForm.originalCoverImageUrl};
    disabled = false;
  }
  if (storyForm.uploadedSelfCoverImage && !confirmedMaterialRight) {
    disabled = true;
  }
  const handlePressConfirmedMaterialRight = React.useCallback(() => {
    setConfirmedMaterialRight(!confirmedMaterialRight);
  }, [confirmedMaterialRight]);
  const [tooltipModalType, setTooltipModal] =
    React.useState<TooltipModalType | null>(null);
  const [imagePickerError, setImagePickerError] = React.useState<string | null>(
    null,
  );
  const tooltipModal = tooltipModalType
    ? TypeToTooltipInfo[tooltipModalType]
    : null;
  return (
    <View style={styles.container}>
      <View style={styles.thumbnailWrapper}>
        <>
          <View style={styles.labelWrapper}>
            <LabelWithOption
              title={'表紙'}
              onPressQuestion={() => {
                setTooltipModal('cover_image');
              }}
            />
            <DefaultOutlineButton
              style={styles.resetButton}
              fontSizeClass={'small'}
              onPress={onRequestReset}>
              リセット
            </DefaultOutlineButton>
          </View>
          <DimensionContext.Consumer>
            {context => {
              const width = context.content.width - MARGIN_HORIZONTAL * 2 - 50;
              const height = width / ASPECT_RATIO;
              return (
                <View style={{width, height}}>
                  <CoverPicker
                    style={{height, width}}
                    source={coverSource}
                    recommendedSize={'2480×3508'}
                    onChangeImage={onChangeImage}
                    onFailImage={setImagePickerError}
                  />
                </View>
              );
            }}
          </DimensionContext.Consumer>
          <View style={styles.editButtons}>
            <DefaultOutlineButton
              style={styles.editButton}
              fontSizeClass={'small'}
              onPress={onForwardToImageSelection}>
              壁紙から選ぶ
            </DefaultOutlineButton>
            <DefaultOutlineButton
              style={styles.editButton}
              fontSizeClass={'small'}
              disabled={!coverSource}
              onPress={onForwardToCharacterImageSelection}>
              キャラ配置
            </DefaultOutlineButton>
            <DefaultOutlineButton
              style={styles.editButton}
              fontSizeClass={'small'}
              disabled={!coverSource}
              onPress={onForwardToTextInput}>
              テキストを入力
            </DefaultOutlineButton>
          </View>
        </>
      </View>
      {onChangeCoverIllustrator && (
        <CoverIllustratorFormField
          value={storyForm.coverIllustrator || ''}
          onChangeText={onChangeCoverIllustrator}
          onPressQuestion={() => {
            setTooltipModal('cover_illustrator');
          }}
        />
      )}
      {storyForm.uploadedSelfCoverImage ? (
        <View style={{marginTop: 16}}>
          <MaterialRightCheck
            checked={confirmedMaterialRight}
            onPress={handlePressConfirmedMaterialRight}
          />
        </View>
      ) : null}
      <View style={styles.button}>
        <EditableLaterMessage />
        <PrimaryButton disabled={disabled} onPress={onSubmit}>
          {buttonName || '次へ'}
        </PrimaryButton>
      </View>
      <AlertModal
        visible={!!imagePickerError}
        onCloseModal={() => setImagePickerError(null)}>
        {imagePickerError}
      </AlertModal>
      <TooltipModal
        visible={tooltipModal ? true : false}
        title={tooltipModal?.title || ''}
        description={tooltipModal?.description || ''}
        source={tooltipModal?.source}
        imageStyle={tooltipModal?.imageStyle}
        onCloseModal={() => {
          setTooltipModal(null);
        }}></TooltipModal>
    </View>
  );
};

export default React.memo(Form);

const IMAGE_HEIGHT = 415;
const IMAGE_WIDTH = 293;
const ASPECT_RATIO = IMAGE_WIDTH / IMAGE_HEIGHT;

const MARGIN_HORIZONTAL = 40;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    marginVertical: 32,
  } as ViewStyle,
  thumbnailWrapper: {
    flex: 1,
    alignItems: 'center',
  } as ViewStyle,
  labelWrapper: {
    flexDirection: 'row',
    height: 30,
    alignSelf: 'stretch',
    marginBottom: 14,
    marginHorizontal: MARGIN_HORIZONTAL,
  } as ViewStyle,
  button: {
    alignItems: 'center',
    paddingVertical: 20,
  } as ViewStyle,
  editButtons: {
    flexDirection: 'row',
    marginVertical: 16,
  } as ViewStyle,
  editButton: {
    borderRadius: 20,
    width: 120,
    height: 40,
    marginHorizontal: 4,
  } as ViewStyle,
  resetButton: {
    borderRadius: 24,
    width: 84,
    height: 32,
    marginHorizontal: 4,
  } as ViewStyle,
});
